import { isEqual } from 'lodash';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { GridColDef, GridPinnedColumnFields } from '@mui/x-data-grid-pro';
import { ColumnDensity } from '@common/types';
import { IUserTableFilters } from '../types';

export type PersistedUserColumn = Pick<
  GridColDef,
  | 'field'
  | 'headerName'
  | 'width'
  | 'minWidth'
  | 'flex'
  | 'sortable'
  | 'filterable'
  | 'disableColumnMenu'
  | 'align'
  | 'headerAlign'
>;

export const initialUserColumns: PersistedUserColumn[] = [
  {
    field: 'name',
    headerName: 'Name',
    minWidth: 220,
    flex: 1,
    sortable: true,
    filterable: false,
  },
  {
    field: 'role',
    headerName: 'Role',
    minWidth: 150,
    flex: 1,
    sortable: false,
    filterable: false,
  },
  {
    field: 'title',
    headerName: 'Title',
    minWidth: 150,
    flex: 1,
    sortable: false,
    filterable: false,
  },
  {
    field: 'team',
    headerName: 'Team',
    minWidth: 180,
    flex: 1,
    sortable: true,
    filterable: false,
  },
  {
    field: 'supervisor',
    headerName: 'Supervisor',
    minWidth: 160,
    flex: 1,
    sortable: true,
    filterable: false,
  },
  {
    field: 'assigned_entities',
    headerName: 'Assigned Entity',
    minWidth: 180,
    flex: 1,
    sortable: false,
    filterable: false,
  },
  {
    field: 'status',
    headerName: 'Status',
    width: 150,
    minWidth: 150,
    sortable: false,
    filterable: false,
  },
  {
    field: 'last_active',
    headerName: 'Last Active',
    width: 160,
    minWidth: 160,
    sortable: true,
    filterable: false,
  },
];

export const initialUserPinnedColumns: GridPinnedColumnFields = {
  left: [],
  right: [],
};

type TUserTableStore = {
  isFiltersCardOpen: boolean;
  handleSetIsFiltersCardOpen: (isOpen: boolean) => void;
  handleToggleFiltersCard: () => void;

  filters: IUserTableFilters;
  draftFilters: IUserTableFilters;

  setFilters: (newFilters: Partial<IUserTableFilters>) => void;
  setDraftFilters: (newFilters: Partial<IUserTableFilters>) => void;

  applyDraftFilters: () => void;
  resetFilters: () => void;
  resetDraftFilters: () => void;
  syncDraftWithAppliedFilters: () => void;
  clearStore: () => void;

  appliedFiltersCount: number;
  draftFiltersCount: number;

  columns: PersistedUserColumn[];
  setColumns: (columns: PersistedUserColumn[]) => void;
  pinnedColumns: GridPinnedColumnFields;
  setPinnedColumns: (pinnedColumns: GridPinnedColumnFields) => void;
  hiddenColumns: string[];
  setHiddenColumns: (hiddenColumns: string[]) => void;
  density: ColumnDensity;
  setDensity: (density: ColumnDensity) => void;
  resetView: () => void;
};

const getDefaultFilters = (): IUserTableFilters => ({
  search: '',
  sort_order: 'asc',
  sort_by: 'name',
  role: '',
  assignedEntities: [],
  page: 1,
  limit: 100,
  status: '',
  assigned_entity_id: '',
  team_id: '',
  assigned_entity_type: '',
  supervisor_id: '',
});

type TKeyOfFilters = keyof IUserTableFilters;

const getAppliedFiltersCount = (
  initialFiltersList: IUserTableFilters,
  appliedFilters: IUserTableFilters,
): number => {
  let count = 0;

  const filtersKeyList: TKeyOfFilters[] = Object.keys(appliedFilters).filter(
    key =>
      key !== 'page' &&
      key !== 'limit' &&
      key !== 'search' &&
      key !== 'sort_order' &&
      key !== 'sort_by',
  ) as TKeyOfFilters[];

  filtersKeyList.forEach(key => {
    if (!isEqual(initialFiltersList[key], appliedFilters[key])) {
      count += 1;
    }
  });

  return count;
};

export const useUserTableStore = create<TUserTableStore>()(
  persist(
    (set, _) => ({
      isFiltersCardOpen: false,

      handleSetIsFiltersCardOpen: isOpen =>
        set(() => ({
          isFiltersCardOpen: isOpen,
        })),

      handleToggleFiltersCard: () =>
        set(state => ({
          isFiltersCardOpen: !state.isFiltersCardOpen,
        })),

      filters: getDefaultFilters(),
      draftFilters: getDefaultFilters(),

      setFilters: newFilters =>
        set(state => {
          const updatedFilters = {
            ...state.filters,
            ...newFilters,
          };

          return {
            filters: updatedFilters,
            appliedFiltersCount: getAppliedFiltersCount(
              getDefaultFilters(),
              updatedFilters,
            ),
          };
        }),

      setDraftFilters: newFilters =>
        set(state => {
          const updatedDraftFilters = {
            ...state.draftFilters,
            ...newFilters,
          };

          return {
            draftFilters: updatedDraftFilters,
            draftFiltersCount: getAppliedFiltersCount(
              getDefaultFilters(),
              updatedDraftFilters,
            ),
          };
        }),

      applyDraftFilters: () =>
        set(state => {
          const appliedFilters = {
            ...state.draftFilters,
            page: 1,
          };

          return {
            filters: appliedFilters,
            appliedFiltersCount: getAppliedFiltersCount(
              getDefaultFilters(),
              appliedFilters,
            ),
          };
        }),

      resetFilters: () => {
        const defaults = getDefaultFilters();

        return set(() => ({
          filters: defaults,
          draftFilters: defaults,
          appliedFiltersCount: 0,
          draftFiltersCount: 0,
        }));
      },

      resetDraftFilters: () => {
        const defaults = getDefaultFilters();

        return set(() => ({
          draftFilters: defaults,
          draftFiltersCount: 0,
        }));
      },

      syncDraftWithAppliedFilters: () =>
        set(state => {
          const preservedFilters = {
            ...getDefaultFilters(),
            sort_by: state.filters.sort_by,
            sort_order: state.filters.sort_order,
          };

          return {
            filters: preservedFilters,
            draftFilters: preservedFilters,
            appliedFiltersCount: 0,
            draftFiltersCount: 0,
            isFiltersCardOpen: false,
          };
        }),

      clearStore: () => {
        const defaults = getDefaultFilters();

        return set(() => ({
          filters: defaults,
          draftFilters: defaults,
          appliedFiltersCount: 0,
          draftFiltersCount: 0,
          isFiltersCardOpen: false,
        }));
      },

      appliedFiltersCount: 0,
      draftFiltersCount: 0,

      columns: initialUserColumns,
      setColumns: columns => set({ columns }),
      pinnedColumns: initialUserPinnedColumns,
      setPinnedColumns: pinnedColumns => set({ pinnedColumns }),
      hiddenColumns: [],
      setHiddenColumns: hiddenColumns => set({ hiddenColumns }),
      density: 'comfortable',
      setDensity: density => set({ density }),
      resetView: () =>
        set({
          columns: initialUserColumns,
          pinnedColumns: initialUserPinnedColumns,
          hiddenColumns: [],
          density: 'comfortable',
        }),
    }),
    {
      name: 'user-table-store',
      partialize: state => ({
        columns: state.columns,
        pinnedColumns: state.pinnedColumns,
        hiddenColumns: state.hiddenColumns,
        density: state.density,
        sort_by: state.filters.sort_by,
        sort_order: state.filters.sort_order,
      }),
      merge: (persistedState, currentState) => {
        const persisted = (persistedState ?? {}) as Partial<
          TUserTableStore & Pick<IUserTableFilters, 'sort_by' | 'sort_order'>
        >;

        const mergedFilters: IUserTableFilters = {
          ...currentState.filters,
          ...(persisted.sort_by && { sort_by: persisted.sort_by }),
          ...(persisted.sort_order && { sort_order: persisted.sort_order }),
        };

        return {
          ...currentState,
          ...persisted,
          filters: mergedFilters,
          draftFilters: mergedFilters,
        };
      },
    },
  ),
);
